import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* App — шелл ядра (kit-v5). Главное меню — двухпанельный ProductRail в sidebar,
   шапка TopbarDesktop/TopbarMobile — full-width НАД Shell (канон v5.7.5,
   kit/v5/src/preview-platform.jsx). Контракт данных — .claude/specs/2026-08-09-v6-menu-contract.md:
   три вызова getInstalledProducts/getProductMenu/openProduct через клиент api/platformMenu,
   ядро не знает ни одного продукта по имени. Файловый распил ответственностей — frame/rail/menu/
   mount/profile/auth/live, App.tsx только собирает их в каркас. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useViewport } from "./lib/viewport";
import { installPrefsBridge } from "./lib/prefsBridge";
import { useAuthStore } from "./state/authStore";
import { t, LocaleProvider } from "./i18n/i18n";
import { useLocale } from "./frame/useLocale";
import { useNumFmt } from "./frame/useNumFmt";
import { startLiveStream } from "./live";
import ModalOverlay from "./components/ModalOverlay";
import RequireAuth from "./components/RequireAuth";
import Login from "./screens/Login";
import { makeLoginApi } from "./screens/loginApi";
import Activate from "./screens/Activate";
import { makeActivateApi } from "./screens/activateApi";
import Maintenance from "./screens/Maintenance";
import { usePlatformStateStore } from "./state/platformStateStore";
import { sharedEventBus } from "./osnovaContext";
import "./screens/Login.css";
import Gate from "./auth/Gate";
import { useAppRouting } from "./auth/useAppRouting";
import { useLogout } from "./auth/useLogout";
import { useProducts } from "./rail/useProducts";
import { useRailOrder } from "./rail/useRailOrder";
import { railProductsFor } from "./rail/railProducts";
import RailNode from "./rail/RailNode";
import MobileDrawer from "./rail/MobileDrawer";
import { useTheme } from "./frame/useTheme";
import { useShellUpdated } from "./frame/useShellUpdated";
import { notifyStateFor } from "./frame/notify";
import Frame from "./frame/Frame";
import WorkArea from "./frame/WorkArea";
import { useProductMenu, STORE_SLUG, PROFILE_TAB_PREFIX } from "./menu/useProductMenu";
import { useProfileDrawer } from "./profile/useProfileDrawer";
import { HelpDrawer } from "@kit-v5";
import { useHelpDrawer } from "./help/useHelpDrawer";
import HelpToasts from "./help/HelpToasts";
// Перехват PUT /api/prefs — доставляет живую смену темы/региона от продуктов ядру
// без правки самих продуктов (lib/prefsBridge.ts); ставится один раз при загрузке модуля.
installPrefsBridge();
function MainShell() {
    const shellRef = useRef(null);
    const vp = useViewport(shellRef);
    const user = useAuthStore((s) => s.user);
    const { products, productsReady } = useProducts();
    const { railOrder, onReorderProducts } = useRailOrder(user?.userId);
    const { theme } = useTheme(user?.userId);
    const locale = useLocale();
    const numFmt = useNumFmt(user?.userId);
    const shellUpdated = useShellUpdated();
    const [modal, setModal] = useState(null);
    // Ref-зеркало modal — openModal читает актуальное состояние без modal в deps: иначе ссылка
    // openModal меняется на КАЖДОЕ открытие/закрытие модалки, а она уходит в profileCtx (useMemo,
    // useProfileDrawer.ts) как зависимость → ящик профиля считал это сменой контекста и
    // перемонтировал экран продукта заново при каждой модалке (B41 — дубли profile-overlay-pane).
    const modalRef = useRef(null);
    useEffect(() => { modalRef.current = modal; }, [modal]);
    const pendingModalReply = useRef(null);
    const openModal = useCallback((args) => {
        return new Promise((resolve, reject) => {
            if (modalRef.current !== null) {
                reject(new Error("BUSY"));
                return;
            }
            pendingModalReply.current = resolve;
            setModal({ args });
        });
    }, []);
    const closeModal = useCallback((result) => {
        setModal(null);
        pendingModalReply.current?.(result);
        pendingModalReply.current = null;
    }, []);
    const { activeEntryId, activeProduct, activeModule, activeItemId, denied, osnovaCtx, navigate, installedProducts, mobileSidebarOpen, setMobileSidebarOpen, shownSlug, railMenu, shownLoading, shownActiveItemId, collapsedMenu, pickProduct, pickItem, openStore, } = useProductMenu({ products, productsReady, user, theme, locale, numFmt, openModal });
    const doLogout = useLogout();
    const help = useHelpDrawer(vp, !!user?.userId);
    const helpOpenView = help.openHelp;
    // Пункт «docs» — внешняя ссылка, кит сам открывает её в новой вкладке (target=_blank);
    // msg/tickets/new открывают HelpDrawer кита, для внешних пунктов делать ничего не нужно.
    const HELP_DRAWER_VIEWS = useMemo(() => new Set(["msg", "tickets", "new"]), []);
    const onHelp = useCallback((item) => {
        if (HELP_DRAWER_VIEWS.has(item.id))
            helpOpenView(item.id);
    }, [HELP_DRAWER_VIEWS, helpOpenView]);
    useEffect(() => {
        if (!user?.userId)
            return;
        return startLiveStream();
    }, [user?.userId]);
    const { profileOpen, profileClosing, profileTab, profileHostRef, profileCtx, requestProfileClose, openProfile, } = useProfileDrawer({ products, installedProducts, theme, locale, numFmt, user, navigate, openModal });
    // Ящик профиля — оверлей поверх рабочей области (position:absolute, ProfileOverlay.tsx): смена
    // двери рельса (activeEntryId) не закрывала его сама — новый продукт монтировался под ним
    // невидимо, панель выглядела «залипшей» на профиле. Клик по обычному пункту меню (тот же дверь,
    // pickItem→setActiveItemId) тоже не звал navigate — оверлей оставался в DOM (z-index 50) и
    // перехватывал клики по всей рабочей области (QA-03); свои вкладки профиля (PROFILE_TAB_PREFIX/
    // PROFILE_MENU_ITEM_ID) activeItemId не трогают, поэтому переключение вкладок ящика его не закрывает.
    useEffect(() => {
        if (profileOpen)
            requestProfileClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeEntryId, activeItemId]);
    const railProducts = useMemo(() => railProductsFor(products, railOrder), [products, railOrder]);
    const notify = useMemo(() => notifyStateFor(products), [products]);
    const railNode = (_jsx(RailNode, { products: productsReady ? railProducts : [], activeProductId: shownSlug, onPickProduct: pickProduct, onReorder: onReorderProducts, menu: railMenu, menuLoading: shownLoading, 
        /* ящик профиля открыт — подсвечиваем «Мой профиль» в меню продукта-владельца
           (design: OSNOVA - Команда.html, activeItemId={profileOpen ? "__me" : section});
           конкретный подпункт (вкладка ящика) — родитель раскрывается сам (kit RailItem, walk по children) */
        activeItemId: profileOpen ? `${PROFILE_TAB_PREFIX}${profileTab}` : shownActiveItemId, onPickItem: pickItem, collapsedMenu: collapsedMenu, onOpenStore: openStore, vp: vp, onClose: () => setMobileSidebarOpen(false) }));
    const productName = activeEntryId === STORE_SLUG ? t("shell.store.title") : (activeProduct?.label ?? t("shell.product.default_title"));
    // Аватар шапки одинаковый на desktop и mobile (kit-v5.6.6) — есть запись профиля (role:"profile")
    // → тогл ящика, активный продукт не меняется; иначе openProfile сам рисует системную заглушку.
    const profile = {
        name: user?.displayName || user?.email,
        status: "online",
        onClick: openProfile,
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: shellRef, className: "osn-platform", style: { width: "100%", height: "100vh" }, children: [_jsx(Frame, { vp: vp, productName: productName, sidebar: railNode, onMenuToggle: () => setMobileSidebarOpen((v) => !v), onHelp: onHelp, onLogout: doLogout, profile: profile, notify: notify, shellUpdated: shellUpdated, helpUnread: help.helpUnread, children: _jsx(WorkArea, { denied: denied, isStore: activeEntryId === STORE_SLUG, productsReady: productsReady, hasProducts: railProducts.length > 0, active: activeEntryId ? { module: activeModule ?? activeEntryId, view: activeItemId ?? undefined } : null, ctx: osnovaCtx, profileOpen: profileOpen, profileClosing: profileClosing, profileHasOwner: !!profileCtx, profileHostRef: profileHostRef, onProfileRequestClose: requestProfileClose }) }), vp === "mobile" && (_jsx(MobileDrawer, { open: mobileSidebarOpen, onClose: () => setMobileSidebarOpen(false), children: railNode }))] }), _jsx(ModalOverlay, { state: modal, onClose: closeModal }), help.mounted && (_jsx(HelpDrawer, { open: help.visible, view: help.view, api: help.api, vp: help.helpVp, onClose: help.closeHelp, onClosed: help.onClosed, onToast: help.onToast, onUnread: help.setHelpUnread, prefill: help.prefill })), _jsx(HelpToasts, { toasts: help.toasts, onDismiss: help.dismissToast })] }));
}
export default function App() {
    const { isLoginPath, isActivatePath, activateToken, handleLoginSuccess, handleActivateSuccess } = useAppRouting();
    const loginApi = useMemo(() => makeLoginApi(), []);
    const activateApi = useMemo(() => makeActivateApi(), []);
    // Маркер состояния платформы (T-SET-5) перекрывает вообще всё — и до входа, и внутри сессии:
    // клиенту нужна честная техстраница, а не голые сетевые ошибки под каждой кнопкой.
    const platformInfo = usePlatformStateStore((s) => s.info);
    // T-SET-RESTORE-PROGRESS-UX: продукт (Настройки, «Вернуться к копии») сообщает о начале
    // восстановления сразу по успешному ответу, той же живой шиной, что platform:section —
    // техстраница появляется немедленно, не дожидаясь следующего опроса /api/status (до 15 с
    // владелец иначе смотрит на пустой экран, будто клик не сработал). Точные данные (шаги,
    // время начала) техстраница уже сама подтянет своим опросом сразу после монтирования.
    useEffect(() => {
        return sharedEventBus.on("platform:maintenance", (data) => {
            const payload = data;
            usePlatformStateStore.getState().set({
                state: payload?.state ?? "restoring",
                reason: payload?.reason ?? "",
                since: payload?.since ?? "",
            });
        });
    }, []);
    if (platformInfo)
        return _jsx(LocaleProvider, { children: _jsx(Maintenance, {}) });
    if (isLoginPath)
        return _jsx(LocaleProvider, { children: _jsx(Login, { api: loginApi, onSuccess: handleLoginSuccess }) });
    if (isActivatePath)
        return _jsx(LocaleProvider, { children: _jsx(Activate, { api: activateApi, token: activateToken, onSuccess: handleActivateSuccess }) });
    return (_jsx(LocaleProvider, { children: _jsx(RequireAuth, { children: _jsx(Gate, { children: _jsx(MainShell, {}) }) }) }));
}
